import { useCallback, useEffect, useRef, useState, type DragEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import { uploadPhotos, getPhotoStatus, ApiError } from "../../lib/api";
import { SiteIcon } from "../../components/site/SiteIcon";
import { BUSINESS } from "../../content/site";

/**
 * Enquiry received (Story #25446, prototype § spec-enquire-confirmation). Restyle of the F1.5-2 screen:
 * the upload limits, the polling and every message below are unchanged.
 *
 * BR-WEB-070 — up to 10 images, 10 MB each, jpeg/png/heic/webp; extra files beyond 10 are ignored.
 * BR-WEB-071 — Pending photos polled every 5 s for up to 2 minutes; Accepted / Scanning… / Not accepted.
 * BR-WEB-072 — without an upload token the page shows the thank-you only (no uploader).
 */
export const MAX_FILES = 10;
export const MAX_BYTES = 10 * 1024 * 1024;
export const ACCEPT = "image/jpeg,image/png,image/heic,image/webp";
const ACCEPTED_TYPES = ACCEPT.split(",");
export const POLL_MS = 5000;
export const POLL_LIMIT_MS = 120_000; // BR-T-006: stop polling after 2 minutes

interface PhotoRow {
  photoId: number;
  fileName: string;
  scanStatus: string; // Pending | Clean | Malicious | Error | Rejected
}

function statusLabel(s: string): { text: string; cls: string } {
  switch (s) {
    case "Clean": return { text: "Accepted", cls: "ok" };
    case "Pending": return { text: "Scanning…", cls: "scan" };
    default: return { text: "Not accepted", cls: "bad" }; // Malicious / Error / Rejected — don't alarm
  }
}

function readToken(hash: string): string {
  // Token travels in the URL fragment (#token=...) so it isn't sent in Referer / server logs.
  const m = /[#&]token=([^&]+)/.exec(hash);
  return m && m[1] ? decodeURIComponent(m[1]) : "";
}

export default function EnquireConfirmationPage() {
  const location = useLocation();
  const token = readToken(location.hash);

  const [photos, setPhotos] = useState<PhotoRow[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollStart = useRef<number | null>(null);

  const onSelect = useCallback(async (files: FileList | File[] | null) => {
    if (!files || files.length === 0 || !token) return;
    setError(null);
    const picked = Array.from(files).slice(0, MAX_FILES);
    const tooBig = picked.find((f) => f.size > MAX_BYTES);
    if (tooBig) {
      setError(`"${tooBig.name}" is larger than 10 MB. Please choose a smaller image.`);
      return;
    }
    setUploading(true);
    try {
      const res = await uploadPhotos(token, picked);
      const rows: PhotoRow[] = (res.photos ?? []).map((p) => ({
        photoId: p.photoId,
        fileName: p.fileName || "photo",
        scanStatus: p.status || "Pending",
      }));
      setPhotos((prev) => [...prev, ...rows]);
      pollStart.current = Date.now();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError("Your upload session has expired. Please submit a new enquiry to add photos.");
      } else {
        setError("Some photos couldn't be uploaded. Please try again.");
      }
    } finally {
      setUploading(false);
    }
  }, [token]);

  // "or drag them here" (prototype). A drop skips the picker's `accept` filter, so apply the same
  // allowed types here before the files reach the unchanged onSelect path.
  const onDrop = useCallback((e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    if (uploading) return;
    void onSelect(Array.from(e.dataTransfer.files).filter((f) => ACCEPTED_TYPES.includes(f.type)));
  }, [onSelect, uploading]);

  // Poll Pending photos every 5s, up to 2 minutes.
  useEffect(() => {
    const hasPending = photos.some((p) => p.scanStatus === "Pending");
    if (!token || !hasPending) return;
    if (pollStart.current === null) pollStart.current = Date.now();

    const interval = window.setInterval(async () => {
      if (pollStart.current !== null && Date.now() - pollStart.current > POLL_LIMIT_MS) {
        window.clearInterval(interval);
        return;
      }
      const pending = photos.filter((p) => p.scanStatus === "Pending");
      const updates = await Promise.all(
        pending.map(async (p) => {
          try {
            const s = await getPhotoStatus(token, p.photoId);
            return { photoId: p.photoId, scanStatus: s.scanStatus || "Pending" };
          } catch {
            return null;
          }
        }),
      );
      setPhotos((prev) =>
        prev.map((row) => {
          const u = updates.find((x) => x && x.photoId === row.photoId);
          return u ? { ...row, scanStatus: u.scanStatus } : row;
        }),
      );
    }, POLL_MS);

    return () => window.clearInterval(interval);
  }, [token, photos]);

  return (
    <div className="gw-done" data-testid="enquire-confirmation">
      <div className="gw-tick">
        <SiteIcon name="check" size={32} strokeWidth={2.4} />
      </div>
      <p className="gw-eyebrow">Enquiry received</p>
      <h1 style={{ fontSize: 46 }}>Thanks — we’ve got your enquiry.</h1>
      <p style={{ fontSize: 18, color: "var(--ink-2)", marginTop: 16 }}>
        A local rep will review it and get back to you within two business days. If it’s urgent, call us
        on <a href={BUSINESS.tel}>{BUSINESS.phone}</a>.
      </p>

      {token ? (
        <section className="gw-box" style={{ marginTop: 36, padding: 30 }} aria-labelledby="gw-photos-h">
          {/* h2 for heading order (h1 → h2); sized as the prototype's .gw-box h3. */}
          <h2 id="gw-photos-h" style={{ fontSize: 22, marginBottom: 10 }}>
            Add photos of your space{" "}
            <span style={{ font: "400 14px var(--sans)", color: "var(--muted)" }}>(optional)</span>
          </h2>
          <p>
            Photos help us prepare a more accurate quote, faster. Up to {MAX_FILES} images, 10 MB each (JPEG,
            PNG, HEIC or WebP).
          </p>
          <label
            className="gw-drop relative focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-[var(--ink)]"
            data-testid="photo-drop"
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
          >
            <SiteIcon name="upload" size={30} />
            <br />
            <b>{uploading ? "Uploading…" : "Choose photos"}</b>
            <br />
            <span style={{ color: "var(--muted)", fontSize: 14 }}>or drag them here</span>
            {/* .gw-drop input is display:none in the shared CSS; keep the input focusable instead. */}
            <input
              type="file"
              accept={ACCEPT}
              multiple
              className="sr-only"
              style={{ display: "block" }}
              aria-label="Photos of your space (optional)"
              disabled={uploading}
              onChange={(e) => onSelect(e.target.files)}
            />
          </label>

          {error && (
            <p className="gw-fine" role="alert" style={{ color: "var(--red)" }}>
              {error}
            </p>
          )}

          {photos.length > 0 && (
            <ul className="gw-files" aria-live="polite" data-testid="photo-list">
              {photos.map((p) => {
                const s = statusLabel(p.scanStatus);
                return (
                  <li key={p.photoId}>
                    <span className="truncate">{p.fileName}</span>
                    <span className={`ml-3 shrink-0 ${s.cls}`}>{s.text}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      ) : null}

      <div className="gw-actions" style={{ marginTop: 32 }}>
        <Link className="gw-btn gw-btn-ink" to="/">
          Back to home
        </Link>
        <Link className="gw-btn gw-btn-line" to="/our-work">
          Browse our work
        </Link>
      </div>
    </div>
  );
}
